import * as babylon from "@babylonjs/core";

import type * as components from "../../components";

/** A world-space axis-aligned box, as the two corners that define it. */
export interface Bounds {
  min: babylon.Vector3;
  max: babylon.Vector3;
}

/**
 * The world-space extent of whatever a Mesh component draws.
 *
 * The one place that knows a mesh component may be a single node *or* a whole
 * model hierarchy. A `sphere`, `box` or `model_node` has bounding info of its
 * own; a `model` is anchored to a `TransformNode`, which has none — its extent
 * is the union of the copy's drawn nodes.
 *
 * Returns `undefined` when there is nothing drawn yet, which is the state every
 * model-backed mesh is in while its file is still loading.
 *
 * Read rather than recomputed. `Node.getHierarchyBoundingVectors` would do this
 * in one call, but it forces a world-matrix recomputation of every descendant
 * and increments the scene's render id to do so — per frame, per model, from
 * inside the camera systems. The world corners here are the ones the last
 * rendered frame left behind, exactly as the single-mesh path has always used.
 */
export function meshBounds(
  mesh: components.Mesh | undefined,
): Bounds | undefined {
  if (mesh?.model) {
    // Child *meshes*, not `nodesByName`: the latter keeps one node per name and
    // a glTF may repeat one, and the frames between the anchor and the geometry
    // have no bounds to contribute anyway.
    return unionBounds(mesh.model.anchor.getChildMeshes(false));
  }
  return boundsOf(mesh?.mesh);
}

/** The world-space extent of one drawn mesh, or `undefined` if there is none. */
export function boundsOf(
  mesh: babylon.AbstractMesh | undefined,
): Bounds | undefined {
  if (!mesh) {
    return undefined;
  }
  const box = mesh.getBoundingInfo().boundingBox;
  return { min: box.minimumWorld, max: box.maximumWorld };
}

/** The smallest box containing every one of `meshes`, if any of them draws. */
function unionBounds(meshes: babylon.AbstractMesh[]): Bounds | undefined {
  let bounds: Bounds | undefined;
  for (const mesh of meshes) {
    if (mesh.getTotalVertices() === 0) {
      continue;
    }
    const box = mesh.getBoundingInfo().boundingBox;
    bounds = bounds
      ? {
          min: babylon.Vector3.Minimize(bounds.min, box.minimumWorld),
          max: babylon.Vector3.Maximize(bounds.max, box.maximumWorld),
        }
      : { min: box.minimumWorld.clone(), max: box.maximumWorld.clone() };
  }
  return bounds;
}

/**
 * The half-size of a box: Babylon's `extendSizeWorld`, for callers that have a
 * {@link Bounds} rather than a `BoundingBox`.
 */
export function extentOf(bounds: Bounds): babylon.Vector3 {
  return bounds.max.subtract(bounds.min).scale(0.5);
}

/**
 * The largest half-extent of a box, which is what every camera formula here
 * calls a bounding radius. Falls back to 1 for nothing drawn, mirroring what
 * those formulas already do for an empty scene.
 */
export function boundingRadiusOf(bounds: Bounds | undefined): number {
  return bounds ? Math.max(...extentOf(bounds).asArray()) : 1;
}
